import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import './styles.css';
import InfiniteMenu from '../components/InfiniteMenu/InfiniteMenu';
import { findSimilarTextures, defaultWeights } from '../components/TextureSimilarity';
import { supabase } from '../lib/supabase';
import { Home } from '@geist-ui/icons';
import { useTheme, Card, Text, Spacer, Tag } from '@geist-ui/core';
import { processTags, getTextureColor } from '../utils/cardStackUtils';

function Page3() {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedTexture = location.state?.texture || null;
  const [loading, setLoading] = useState(true);
  const [menuItems, setMenuItems] = useState([]);
  const [allTextures, setAllTextures] = useState([]);
  // Aggiungiamo uno stato per la texture corrente
  const [currentTexture, setCurrentTexture] = useState(null);
  // Aggiungiamo stati per la transizione fluida
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  // Aggiungiamo uno stato per la cronologia delle texture selezionate
  const [textureHistory, setTextureHistory] = useState([]);
  // Stato per la UI dei bottoni durante la transizione
  const [uiTransitioning, setUiTransitioning] = useState(false);
  // Aggiungiamo uno stato per la dissolvenza iniziale
  const [initialFadeComplete, setInitialFadeComplete] = useState(false);
  // Ref per tenere traccia dell'istanza del menu
  const menuRef = useRef(null);
  // Ref per memorizzare l'ultimo menu attivo
  const lastActiveMenuRef = useRef(null);
  // Variabile che indica quante texture simili cercare
  const SIMILAR_TEXTURES_COUNT = 50;
  // Theme for styling
  const theme = useTheme();
  const isDark = theme.type === 'customDark';

  // Add state for texture info card visibility
  const [detailCardVisible, setDetailCardVisible] = useState(false);
  const [centeredTexture, setCenteredTexture] = useState(null);

  // Add state to track the currently focused texture in the Infinite Menu
  const [focusedItem, setFocusedItem] = useState(null);

  // Function to generate a unique color for each texture
  const generateColor = useCallback((texture, index = 0) => {
    // If texture has an average_color and it's in hex format, use it
    if (texture.average_color && texture.average_color.startsWith('#')) {
      return texture.average_color.substring(1);
    }
    
    // Otherwise generate a color based on texture properties
    const hashString = (str) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      return Math.abs(hash);
    };
    
    // Generate a unique value based on texture name and material
    const uniqueValue = hashString(texture.texture_name + texture.material_name + index);
    
    // Convert to a hex color
    let color = (uniqueValue & 0xFFFFFF).toString(16);
    
    // Ensure 6 characters for the color
    while (color.length < 6) {
      color = '0' + color;
    }
    
    return color;
  }, []);
  
  // DEBUG function to print the menuItems to console
  useEffect(() => {
    if (menuItems.length > 0) {
      console.log("Menu items generated:", menuItems);
    }
  }, [menuItems]);

  // Load all textures from Supabase
  useEffect(() => {
    const fetchTextures = async () => {
      try {
        console.log('Fetching textures from Supabase...');
        const { data, error } = await supabase.from('texture_duplicate').select('*');
        
        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }
        
        console.log('Textures fetched successfully:', data?.length || 0, 'textures');
        setAllTextures(data || []);
      } catch (err) {
        console.error('Error fetching textures:', err);
        setAllTextures([]);
      }
    };

    fetchTextures();
  }, []);

  // Process textures once both allTextures and selectedTexture are available
  useEffect(() => {
    if (!selectedTexture) {
      console.log("No selected texture, navigating to home");
      navigate('/');
      return;
    }

    if (allTextures.length === 0) {
      console.log("Waiting for textures to load...");
      return;
    }

    console.log("Selected texture:", selectedTexture);
    console.log("All textures loaded:", allTextures.length);

    // Imposta la texture corrente
    setCurrentTexture(selectedTexture);
    
    // Aggiungi la texture iniziale alla cronologia
    setTextureHistory([selectedTexture]);
    
    // Process and find similar textures
    processTextures(selectedTexture, allTextures);
    
    // Inizia con fadeOut impostato a true per poi mostrare l'effetto di dissolvenza in entrata
    setFadeOut(true);
    
    // Dopo aver preparato i dati, mostriamo il grafico con un effetto di dissolvenza
    setTimeout(() => {
      setLoading(false);
      
      // Un piccolo ritardo prima di rimuovere la classe fade-out
      setTimeout(() => {
        // Inizia la dissolvenza in entrata
        setFadeOut(false);
        
        // Segnaliamo che la dissolvenza iniziale è stata completata
        // dopo il tempo di transizione + transition-delay
        setTimeout(() => {
          setInitialFadeComplete(true);
        }, 1300); // 500ms (durata transizione) + 800ms (delay) = 1300ms
      }, 100);
    }, 500);
  }, [selectedTexture, allTextures, navigate]);

  // Function to update centered texture info
  const updateCenteredTexture = useCallback((texture) => {
    setCenteredTexture(texture);
    setDetailCardVisible(true);
  }, []);

  // Function to handle when a texture comes into focus in the Infinite Menu
  const handleFocusChange = useCallback((item) => {
    if (item && item.textureData) {
      console.log("Focus changed to:", item.textureData.texture_name);
      
      // Aggiorna lo stato con il nuovo item in focus
      setFocusedItem(item);
      
      // Aggiorna la card con i dati della texture centrata
      setCenteredTexture(item.textureData);
      
      // Assicurati che la card sia visibile
      if (!detailCardVisible) {
        setDetailCardVisible(true);
      }
    }
  }, [detailCardVisible]);

  // Hide detail card during transitions
  useEffect(() => {
    if (isTransitioning) {
      setDetailCardVisible(false);
    }
  }, [isTransitioning]);

  // Update centered texture when menu items change
  useEffect(() => {
    if (menuItems.length > 0 && currentTexture) {
      const selectedItem = menuItems.find(item => item.isSelected);
      if (selectedItem && selectedItem.textureData) {
        updateCenteredTexture(selectedItem.textureData);
      }
    }
  }, [menuItems, currentTexture, updateCenteredTexture]);

  // Function to salvare il riferimento al menu
  const saveMenuRef = useCallback((instance) => {
    // Salva il riferimento all'istanza InfiniteMenu
    if (instance && instance.sketchRef && instance.sketchRef.current) {
      menuRef.current = instance.sketchRef.current;
      console.log("Menu reference saved");
    }
  }, []);

  // Funzione per rigenerare il menu con una nuova texture come selezionata
  const regenerateWithCurrentTexture = (newSelectedTexture) => {
    if (!newSelectedTexture || allTextures.length === 0 || isTransitioning) return;
    
    // Avvia la transizione
    setIsTransitioning(true);
    
    // Memorizza l'istanza corrente del menu per l'UI
    lastActiveMenuRef.current = menuRef.current;
    
    // Blocca l'UI dei bottoni durante la transizione
    setUiTransitioning(true);
    
    // Fase 1: Dissolvenza in uscita
    setFadeOut(true);
    
    // Prepara i nuovi dati ma non ancora visualizzati
    const prepareNewTextures = () => {
      // Aggiorna la texture corrente
      setCurrentTexture(newSelectedTexture);
      
      // Aggiungi alla cronologia se non è già presente o se è diversa dall'ultima
      setTextureHistory(prevHistory => {
        // Verifica se la texture è già l'ultima nella cronologia
        if (prevHistory.length > 0 && prevHistory[prevHistory.length - 1].id === newSelectedTexture.id) {
          return prevHistory; // Non aggiungere duplicati consecutivi
        }
        
        // Aggiungi alla cronologia, limitando a max 10 elementi
        const newHistory = [...prevHistory, newSelectedTexture];
        return newHistory.slice(-10);
      });
      
      // Rigenera il menu con la nuova texture come selezionata
      processTextures(newSelectedTexture, allTextures);
    };
    
    // Fase 2: Attendi che la dissolvenza in uscita sia completata
    // Tempo ridotto per velocizzare la transizione
    setTimeout(() => {
      // Mostra il loader mentre prepariamo i nuovi dati
      setLoading(true);
      
      // Prepara i dati per il nuovo grafico
      prepareNewTextures();
      
      // Fase 3: Prepara la dissolvenza in entrata
      // Tempo di attesa ridotto
      setTimeout(() => {
        setLoading(false);
        
        // Piccolo ritardo prima di mostrare il nuovo grafico
        setTimeout(() => {
          // IMPORTANTE: Prima di iniziare la dissolvenza in entrata, centra l'icosaedro
          // in modo che appaia già nella posizione corretta quando diventa visibile
          if (menuRef.current) {
            menuRef.current.centerOnInitialTexture();
            console.log("Posizionando icosaedro prima della dissolvenza in entrata");
          }
          
          // Un piccolo ritardo per assicurarsi che il posizionamento sia applicato
          setTimeout(() => {
            // Reset della flag per la dissolvenza iniziale per assicurare l'effetto
            setInitialFadeComplete(false);
            
            // Ora inizia la dissolvenza in entrata con l'icosaedro già posizionato correttamente
            setFadeOut(false);
            
            // Termina la transizione dopo che la dissolvenza in entrata è completata
            // considerando sia la durata della transizione che il delay
            setTimeout(() => {
              setIsTransitioning(false);
              // Ripristina l'UI dei bottoni e segna il completamento della dissolvenza
              setUiTransitioning(false);
              setInitialFadeComplete(true);
              console.log("Transizione completata, UI ripristinata");
            }, 1300); // 500ms (transizione) + 800ms (delay) = 1300ms
          }, 30); // Ridotto il ritardo per il posizionamento
        }, 50); // Ridotto il ritardo prima di mostrare il nuovo grafico
      }, 250); // Ridotto il tempo di attesa
    }, 500); // Ridotto il tempo per la dissolvenza in uscita
  };

  // Function to process textures and find similar ones
  const processTextures = useCallback((mainTexture, texturesArray) => {
    // Find the index of the selected texture in all textures
    const textureIndex = texturesArray.findIndex(tex => tex.id === mainTexture.id);
    
    if (textureIndex === -1) {
      console.error('Selected texture not found in textures array');
      // Fallback: Use the first texture from the array
      const fallbackItems = createMenuItems(texturesArray[0], 0, [], texturesArray);
      setMenuItems(fallbackItems);
      setLoading(false);
      return;
    }

    console.log("Found texture index:", textureIndex);

    // Utilizzo i pesi predefiniti da TextureSimilarity.js
    const weights = { ...defaultWeights };
    console.log("Using weights from TextureSimilarity:", weights);
    
    // Uso la variabile per definire quante texture simili cercare
    const similarItems = findSimilarTextures(texturesArray, textureIndex, SIMILAR_TEXTURES_COUNT, weights);
    console.log("Similar items found:", similarItems.length);
    
    // Genero gli elementi del menu con la texture selezionata in cima
    const items = createMenuItems(mainTexture, textureIndex, similarItems, texturesArray);
    console.log("Menu items created:", items.length);
    
    // Aggiorno lo stato con i nuovi elementi
    setMenuItems(items);
  }, []);

  // Helper function to create menu items
  const createMenuItems = (mainTexture, textureIndex, similarItems = [], texturesArray = []) => {
    // Create items for InfiniteMenu - selected texture first with isSelected property
    const items = [
      {
        image: `https://ik.imagekit.io/materia/${mainTexture.id}_p.jpeg`,
        fallbackImage: `https://singlecolorimage.com/get/${generateColor(mainTexture)}/1000x1000`,
        title: mainTexture.texture_name,
        description: `${mainTexture.material_name} | ${mainTexture.category_name} | 👁️ TEXTURE SELEZIONATA`,
        link: `/page2/?id=${mainTexture.id}`,
        isSelected: true, // Indica che questa è la texture selezionata
        similarity: 1.0, // La texture selezionata ha similarità massima con se stessa
        textureData: mainTexture // Aggiungiamo i dati completi della texture
      }
    ];

    // If we have similar items, add them to the array
    if (similarItems.length > 0) {
      // Add similar textures to the items array
      similarItems.forEach((item, index) => {
        const texture = item.texture;
        // La similarità è già calcolata come (1 - item.total)
        const similarityScore = (1 - item.total).toFixed(2);
        items.push({
          image: `https://ik.imagekit.io/materia/${texture.id}_p.jpg`,
          fallbackImage: `https://singlecolorimage.com/get/${generateColor(texture, index + 1)}/1000x1000`,
          title: texture.texture_name,
          description: `${texture.material_name} | ${texture.category_name} | Similarità: ${similarityScore}`,
          link: `/page2/?id=${texture.id}`,
          isSelected: false,
          similarity: parseFloat(similarityScore), // Salva il valore di similarità per dimensionare i punti
          textureData: texture // Aggiungiamo i dati completi della texture
        });
      });
    } else if (textureIndex !== null) {
      // If we don't have similar items but have a texture index, generate random similar textures
      const availableTextures = [...texturesArray]; // Usa texturesArray invece di allTextures
      // Remove the selected texture from the available textures
      availableTextures.splice(textureIndex, 1);
      
      // Randomly select textures using the SIMILAR_TEXTURES_COUNT variable
      for (let i = 0; i < SIMILAR_TEXTURES_COUNT; i++) {
        if (availableTextures.length === 0) break;
        
        const randomIndex = Math.floor(Math.random() * availableTextures.length);
        const texture = availableTextures.splice(randomIndex, 1)[0];
        
        // Genera un valore casuale di similarità per le texture casuali
        const randomSimilarity = (Math.random() * 0.7).toFixed(2); // Max 0.7 per distinguerle dalla selezionata
        
        items.push({
          image: `https://ik.imagekit.io/materia/${texture.id}_p.jpeg`,
          fallbackImage: `https://singlecolorimage.com/get/${generateColor(texture, i + 1)}/1000x1000`,
          title: texture.texture_name,
          description: `${texture.material_name} | ${texture.category_name} | Similarità: ${randomSimilarity}`,
          link: `/page2/?id=${texture.id}`,
          isSelected: false,
          similarity: parseFloat(randomSimilarity), // Salva il valore di similarità per dimensionare i punti
          textureData: texture // Aggiungiamo i dati completi della texture
        });
      }
    }

    return items;
  };

  // Function to handle menu item click
  const handleMenuItemClick = (item, action = '') => {
    console.log("Menu interaction:", item, "Action:", action);
    
    // We don't hide the detail card here, as we want it to update for the newly centered item
    // Instead, we'll update it with the new item's data
    if (item && item.textureData) {
      updateCenteredTexture(item.textureData);
    }
    
    // Se è specificata un'azione, gestisci in base al tipo
    if (action === 'regenerate') {
      // Rigenerazione del grafico con la texture come selezionata
      if (item.textureData && !item.isSelected) {
        console.log("Regenerating menu with new selected texture:", item.textureData.texture_name);
        regenerateWithCurrentTexture(item.textureData);
      }
    } else if (action === 'navigate') {
      // Navigazione alla pagina dettagliata della texture (scheda tecnica)
      if (item.textureData) {
        console.log("Navigating to technical sheet with texture data:", item.textureData.texture_name);
        // Passa l'intero oggetto texture nello state, come in cardStackUtils.handleGoToTechnicalSheet
        navigate('/page2', { state: { texture: item.textureData } });
      } else if (item.link) {
        // Fallback al link diretto se non abbiamo i dati completi per qualche motivo
        console.log("Navigating to texture detail page using direct link:", item.link);
        navigate(item.link);
      }
    } else {
      // Comportamento predefinito quando si clicca sulla texture stessa (non sui bottoni)
      if (item.textureData && !item.isSelected) {
        // Se clicchiamo su una texture non selezionata, la rendiamo la nuova texture selezionata
        console.log("Regenerating menu with new selected texture:", item.textureData.texture_name);
        regenerateWithCurrentTexture(item.textureData);
      } else if (item.link) {
        // Se clicchiamo sulla texture selezionata o non abbiamo dati completi, navigiamo al link
        navigate(item.link);
      }
    }
  };

  // Handle texture history item click
  const handleHistoryItemClick = (texture) => {
    // Hide detail card during transition
    setDetailCardVisible(false);
    
    // Verifica che la texture cliccata non sia quella corrente
    if (currentTexture && texture.id === currentTexture.id) return;
    
    // Quando navighiamo nella cronologia, NON aggiungiamo un nuovo elemento alla history
    // Dobbiamo solo cambiare la texture corrente e rigenerare il grafico
    setIsTransitioning(true);
    setFadeOut(true);
    setCurrentTexture(texture);
    
    // Fase 2: Dopo la dissolvenza, genera il nuovo grafico
    setTimeout(() => {
      setLoading(true); // Mostra il loader solo brevemente per dare feedback
      
      // Reset della flag per la dissolvenza iniziale per assicurare l'effetto
      setInitialFadeComplete(false);
      
      // Rigenera il menu con la texture dalla cronologia come selezionata
      processTextures(texture, allTextures);
      
      // Fase 3: Prepara la dissolvenza in entrata
      setTimeout(() => {
        setFadeOut(false);
        setLoading(false);
        
        // Fase 4: Dopo la comparsa del nuovo grafico, centra la vista sulla texture selezionata
        setTimeout(() => {
          if (menuRef.current) {
            // Centra la vista sulla texture selezionata
            menuRef.current.centerOnInitialTexture();
            console.log("Centering view on history texture");
          }
          
          // Fine della transizione e completamento della dissolvenza
          // considerando sia la durata della transizione che il delay
          setTimeout(() => {
            setIsTransitioning(false);
            setInitialFadeComplete(true);
            console.log("Transizione della cronologia completata");
          }, 1300); // 500ms (transizione) + 800ms (delay) = 1300ms
        }, 300);
      }, 200);
    }, 300);
  };

  console.log("Rendering with menuItems:", menuItems.length);
  
  return (
    <div className="discovery-page full-screen" style={{
      backgroundColor: isDark ? '#222' : '#ffffff',
      color: isDark ? '#eaeaea' : '#333',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100vw',
      height: '100vh',
      margin: 0,
      padding: 0,
      overflow: 'hidden'
    }}>
      {/* Loading container - sempre presente ma mostrato/nascosto con fade */}
      <div className={`loading-container ${loading ? 'fade-in' : 'fade-out'}`} style={{
        backgroundColor: isDark ? 'rgba(34, 34, 34, 0.9)' : 'rgba(255, 255, 255, 0.9)'
      }}>
        <div className="loader"></div>
        <div className="loading-text" style={{ color: isDark ? '#eaeaea' : '#333' }}>
          Generando grafico texture...
        </div>
      </div>
      
      {/* Home button in top-left corner, matching Page2 styling */}
      <Link to="/" style={{
        position: 'fixed',
        top: '20px',
        left: '20px',
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        background: isDark ? '#444' : '#eaeaea',
        borderRadius: '50%',
        transition: 'all 0.3s ease',
        zIndex: 2000,
        boxShadow: isDark 
          ? '0 0 15px rgba(100, 100, 255, 0.3), 0 0 5px rgba(150, 150, 255, 0.2)' 
          : '0 5px 10px rgba(0, 0, 0, 0.15)'
      }}>
        <Home size={22} color={isDark ? '#fff' : '#000'} />
      </Link>

      <div className={`infinite-menu-container full-screen-menu ${fadeOut ? 'fade-out' : 'fade-in'}`} style={{
        backgroundColor: 'transparent',
        boxShadow: isDark 
          ? '0 0 20px rgba(0, 0, 0, 0.5)' 
          : '0 4px 20px rgba(0, 0, 0, 0.2)'
      }}>
        {!loading && currentTexture && menuItems.length > 0 ? (
          <InfiniteMenu
            key={currentTexture.id}
            items={menuItems}
            onItemClick={handleMenuItemClick}
            ref={saveMenuRef}
            initialCenteredItem={currentTexture}
            isUITransitioning={uiTransitioning || !initialFadeComplete}
            lastActiveMenu={lastActiveMenuRef.current}
            onCenterChange={handleFocusChange}
            //isDarkTheme={isDark}
          />
        ) : (!loading && !menuItems.length && (
          <div className="error-message" style={{
            color: isDark ? '#eaeaea' : '#333'
          }}>
            <p>Nessuna texture trovata. Riprova più tardi.</p>
          </div>
        ))}
      </div>

      {/* Texture Detail Card - updated to show info about the focused texture */}
      {centeredTexture && (
        <div className={`texture-detail-panel ${detailCardVisible ? 'visible' : ''}`} style={{ 
          position: 'fixed',
          left: '20px', 
          bottom: '20px',
          maxWidth: '350px',
          width: '30%',
          zIndex: 2000,
          transition: 'all 0.3s ease',
          opacity: detailCardVisible ? 1 : 0,
          transform: detailCardVisible ? 'translateY(0)' : 'translateY(20px)',
          pointerEvents: detailCardVisible ? 'all' : 'none'
        }}>
          <Card shadow={false} width="100%" style={{ 
            borderRadius: '20px', 
            overflow: 'hidden',
            background: isDark ? '#333' : undefined,
            border: isDark ? '1px solid #444' : undefined,
            boxShadow: isDark 
              ? '0 0 15px rgba(100, 100, 255, 0.25), 0 0 8px rgba(150, 150, 255, 0.15)' 
              : '0 8px 16px rgba(0, 0, 0, 0.1)'
          }}>
            <Card.Content style={{ 
              padding: '20px',
              background: isDark ? '#333' : undefined,
              color: isDark ? '#eaeaea' : undefined
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                marginBottom: '10px'
              }}>
                <Text h4 my={0}>{centeredTexture.texture_name}</Text>
              </div>

              <div style={{ 
                height: '4px', 
                backgroundColor: centeredTexture.average_color || getTextureColor(centeredTexture), 
                borderRadius: '8px',
                marginBottom: '15px'
              }} />

              {/* Material and category row */}
              <div style={{ 
                display: 'flex', 
                gap: '15px',
                marginBottom: '15px'
              }}>
                <div style={{ flex: 1 }}>
                  <Text small b style={{ color: isDark ? '#999' : '#666', marginBottom: '4px' }}>Materiale</Text>
                  <Text style={{ color: isDark ? '#eaeaea' : '#333', margin: 0 }}>{centeredTexture.material_name}</Text>
                </div>
                <div style={{ flex: 1 }}>
                  <Text small b style={{ color: isDark ? '#999' : '#666', marginBottom: '4px' }}>Categoria</Text>
                  <Text style={{ color: isDark ? '#eaeaea' : '#333', margin: 0 }}>{centeredTexture.category_name}</Text>
                </div>
              </div>

              {/* Similarity info - show if available from focused item */}
              {focusedItem && !focusedItem.isSelected && (
                <div style={{ marginBottom: '15px' }}>
                  <Text small b style={{ color: isDark ? '#999' : '#666', marginBottom: '4px' }}>Similarità</Text>
                  
                  {/* Calcola il valore di similarità, gestendo i valori negativi */}
                  {(() => {
                    // Normalizza il valore: se è negativo imposta a 0
                    const similarityValue = Math.max(0, focusedItem.similarity);
                    // Percentuale da 0 a 100 per la barra di progresso
                    const percentValue = Math.round(similarityValue * 100);
                    // Punteggio da 1 a 5 (mappa da 0-1 a 1-5, ma garantendo almeno 1 se > 0)
                    const ratingValue = similarityValue > 0 ? 
                      Math.max(1, Math.round(similarityValue * 5)) : 0;
                    
                    // Genera i pallini invece delle stelle
                    const dots = [];
                    for (let i = 1; i <= 5; i++) {
                      dots.push(
                        <span 
                          key={i} 
                          style={{ 
                            color: i <= ratingValue 
                              ? (isDark ? '#eaeaea' : '#333') 
                              : (isDark ? '#444' : '#ddd'),
                            fontSize: '18px',
                            marginRight: '3px'
                          }}
                        >
                          {i <= ratingValue ? '●' : '○'}
                        </span>
                      );
                    }
                    
                    return (
                      <>
                        {/* Barra di progresso */}
                        <div style={{
                          width: '100%',
                          height: '8px',
                          backgroundColor: isDark ? '#444' : '#eaeaea',
                          borderRadius: '4px',
                          marginBottom: '8px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${percentValue}%`,
                            height: '100%',
                            backgroundColor: isDark ? '#666' : '#888',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                        
                        {/* Valore numerico e pallini */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          {/* Percentuale */}
                          <Text style={{ color: isDark ? '#eaeaea' : '#333', margin: 0, fontWeight: 'bold' }}>
                            {percentValue}%
                  </Text>
                          
                          {/* Pallini */}
                          <div>{dots}</div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Description */}
              {centeredTexture.description && (
                <div style={{ marginBottom: '15px' }}>
                  <Text small b style={{ color: isDark ? '#999' : '#666', marginBottom: '4px' }}>Descrizione</Text>
                  <Text style={{ color: isDark ? '#eaeaea' : '#333', margin: 0 }}>{centeredTexture.description}</Text>
                </div>
              )}

              <Spacer h={0.5} />

              {/* Tags sections */}
              {(() => {
                const tactileTags = processTags(centeredTexture.tactile_tags);
                const aestheticTags = processTags(centeredTexture.aesthetic_tags);
                
                return (
                  <>
                    {tactileTags.length > 0 && (
                      <div style={{ marginBottom: '10px' }}>
                        <Text small b style={{ color: isDark ? '#999' : '#666', marginBottom: '4px' }}>Tag Tattili</Text>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                          {tactileTags.map((tag, idx) => (
                            <Tag 
                              key={`tactile-${idx}`} 
                              type="lite"
                              style={{ 
                                borderRadius: '20px',
                                backgroundColor: isDark ? '#3a3a3a' : '#f5f5f5',
                                color: isDark ? '#999' : '#666'
                              }}
                            >
                              {tag}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    )}

                    {aestheticTags.length > 0 && (
                      <div style={{ marginBottom: '10px' }}>
                        <Text small b style={{ color: isDark ? '#999' : '#666', marginBottom: '4px' }}>Tag Estetici</Text>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                          {aestheticTags.map((tag, idx) => (
                            <Tag 
                              key={`aesthetic-${idx}`} 
                              type="lite"
                              style={{ 
                                borderRadius: '20px',
                                backgroundColor: isDark ? '#3a3a3a' : '#f5f5f5',
                                color: isDark ? '#999' : '#666'
                              }}
                            >
                              {tag}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </Card.Content>
          </Card>
        </div>
      )}

      {/* Texture Breadcrumbs */}
      {textureHistory.length > 0 && (
        <div className="texture-breadcrumbs" style={{
          backgroundColor: isDark ? 'rgba(18, 18, 18, 0.8)' : 'rgba(245, 245, 245, 0.8)',
          boxShadow: isDark 
            ? '0 0 15px rgba(0, 0, 0, 0.4)' 
            : '0 4px 10px rgba(0, 0, 0, 0.1)'
        }}>
          <div className="breadcrumbs-list">
            {textureHistory.length <= 3 ? (
              // Se abbiamo 3 o meno elementi, mostra tutti
              textureHistory.map((texture, index) => {
                const isActive = currentTexture && texture.id === currentTexture.id;
                const isLast = index === textureHistory.length - 1;
                
                return (
                  <React.Fragment key={`${texture.id}-${index}`}>
                    <div 
                      className={`breadcrumb-item ${isActive ? 'active' : ''}`}
                      onClick={() => handleHistoryItemClick(texture)}
                      title={`${texture.texture_name} - ${texture.material_name}`}
                      style={{
                        backgroundColor: isActive 
                          ? (isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)') 
                          : (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                        color: isDark ? '#eaeaea' : '#333'
                      }}
                    >
                      <div 
                        className="breadcrumb-color" 
                        style={{ backgroundColor: `#${texture.average_color ? texture.average_color.replace('#', '') : generateColor(texture)}` }}
                      ></div>
                      <span className="breadcrumb-name" style={{ color: isDark ? '#eaeaea' : '#000' }}>{texture.texture_name}</span>
                    </div>
                    {!isLast && (
                      <div className="breadcrumb-separator" style={{
                        color: isDark ? '#888' : '#666' 
                      }}>
                        <span>›</span>
                      </div>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              // Se abbiamo più di 3 elementi, mostra il primo, i puntini di sospensione e gli ultimi due
              <>
                {/* Primo elemento */}
                <div 
                  className={`breadcrumb-item ${currentTexture && textureHistory[0].id === currentTexture.id ? 'active' : ''}`}
                  onClick={() => handleHistoryItemClick(textureHistory[0])}
                  title={`${textureHistory[0].texture_name} - ${textureHistory[0].material_name}`}
                  style={{
                    backgroundColor: currentTexture && textureHistory[0].id === currentTexture.id
                      ? (isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)') 
                      : (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                    color: isDark ? '#eaeaea' : '#333'
                  }}
                >
                  <div 
                    className="breadcrumb-color" 
                    style={{ backgroundColor: `#${textureHistory[0].average_color ? textureHistory[0].average_color.replace('#', '') : generateColor(textureHistory[0])}` }}
                  ></div>
                  <span className="breadcrumb-name" style={{ color: isDark ? '#eaeaea' : '#000' }}>{textureHistory[0].texture_name}</span>
                </div>
                
                {/* Separatore */}
                <div className="breadcrumb-separator" style={{
                  color: isDark ? '#888' : '#666'
                }}>
                  <span>›</span>
                </div>
                
                {/* Puntini di sospensione con dropdown per gli elementi nascosti */}
                <div className="breadcrumb-ellipsis-container">
                  <div className="breadcrumb-ellipsis" style={{
                    color: isDark ? '#eaeaea' : '#333',
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                  }}>...</div>
                  <div className="hidden-breadcrumbs" style={{
                    backgroundColor: isDark ? '#333' : '#fff',
                    boxShadow: isDark 
                      ? '0 0 15px rgba(0, 0, 0, 0.5)' 
                      : '0 4px 15px rgba(0, 0, 0, 0.2)'
                  }}>
                    {textureHistory.slice(1, -2).map((texture, index) => (
                      <div 
                        key={`hidden-${texture.id}-${index}`}
                        className={`hidden-breadcrumb-item ${currentTexture && texture.id === currentTexture.id ? 'active' : ''}`}
                        onClick={() => handleHistoryItemClick(texture)}
                        title={`${texture.texture_name} - ${texture.material_name}`}
                        style={{
                          backgroundColor: currentTexture && texture.id === currentTexture.id
                            ? (isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)') 
                            : 'transparent',
                          color: isDark ? '#eaeaea' : '#333'
                        }}
                      >
                        <div 
                          className="breadcrumb-color" 
                          style={{ backgroundColor: `#${texture.average_color ? texture.average_color.replace('#', '') : generateColor(texture)}` }}
                        ></div>
                        <span className="breadcrumb-name" style={{ color: isDark ? '#eaeaea' : '#000' }}>{texture.texture_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Separatore */}
                <div className="breadcrumb-separator" style={{
                  color: isDark ? '#888' : '#666'
                }}>
                  <span>›</span>
                </div>
                
                {/* Penultimo elemento */}
                <div 
                  className={`breadcrumb-item ${currentTexture && textureHistory[textureHistory.length - 2].id === currentTexture.id ? 'active' : ''}`}
                  onClick={() => handleHistoryItemClick(textureHistory[textureHistory.length - 2])}
                  title={`${textureHistory[textureHistory.length - 2].texture_name} - ${textureHistory[textureHistory.length - 2].material_name}`}
                  style={{
                    backgroundColor: currentTexture && textureHistory[textureHistory.length - 2].id === currentTexture.id
                      ? (isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)') 
                      : (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                    color: isDark ? '#eaeaea' : '#333'
                  }}
                >
                  <div 
                    className="breadcrumb-color" 
                    style={{ backgroundColor: `#${textureHistory[textureHistory.length - 2].average_color ? textureHistory[textureHistory.length - 2].average_color.replace('#', '') : generateColor(textureHistory[textureHistory.length - 2])}` }}
                  ></div>
                  <span className="breadcrumb-name" style={{ color: isDark ? '#eaeaea' : '#000' }}>{textureHistory[textureHistory.length - 2].texture_name}</span>
                </div>
                
                {/* Separatore */}
                <div className="breadcrumb-separator" style={{
                  color: isDark ? '#888' : '#666'
                }}>
                  <span>›</span>
                </div>
                
                {/* Ultimo elemento */}
                <div 
                  className={`breadcrumb-item ${currentTexture && textureHistory[textureHistory.length - 1].id === currentTexture.id ? 'active' : ''}`}
                  onClick={() => handleHistoryItemClick(textureHistory[textureHistory.length - 1])}
                  title={`${textureHistory[textureHistory.length - 1].texture_name} - ${textureHistory[textureHistory.length - 1].material_name}`}
                  style={{
                    backgroundColor: currentTexture && textureHistory[textureHistory.length - 1].id === currentTexture.id
                      ? (isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)') 
                      : (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                    color: isDark ? '#eaeaea' : '#333'
                  }}
                >
                  <div 
                    className="breadcrumb-color" 
                    style={{ backgroundColor: `#${textureHistory[textureHistory.length - 1].average_color ? textureHistory[textureHistory.length - 1].average_color.replace('#', '') : generateColor(textureHistory[textureHistory.length - 1])}` }}
                  ></div>
                  <span className="breadcrumb-name" style={{ color: isDark ? '#eaeaea' : '#000' }}>{textureHistory[textureHistory.length - 1].texture_name}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Page3; 